import { Maybe } from '@graphql-tools/utils'
import isString from 'lodash/isString'
import locales from '@/constants/locales'
import { slugs } from '@/constants/slugs'
import { StartWatchingProject } from '@/services/ProjectsService/ProjectsService'
import { normalizeProjectSeasons } from '@/services/ProjectsService/normalizeSeasons'
import { LinkViewModel, MapDataOptions, PageModel } from '@/services/RenderService/types'
import {
  isCollectionDisplay,
  isCollectionModel,
  isCuratedCollection,
  isValidCuratedCollection,
} from '@/services/RenderService/validations'
import { ContentExperiment, PageContentItem } from '@/types/codegen-contentful'
import { buildLinkToEpisode } from '@/utils/EpisodeUtil'
import { isProjectExcludedFromStartWatching } from '@/utils/SpecialProjectUtils/SpecialProjectUtils'
import { logger } from '@/utils/logging'
import { isDefined } from '@/utils/primitives/generics'

export function getCollectionDataSources(page: PageModel): Set<string> {
  if (!page?.contentCollection?.items) return new Set()
  const dataSources: string[] =
    page.contentCollection.items.reduce(reduceDataSourcesFromContentItem, []).filter(isDefined) ?? []
  return new Set(dataSources)
}

export function reduceDataSourcesFromContentItem(dataSources: string[], contentItem: Maybe<PageContentItem>): string[] {
  if (isCollectionDisplay(contentItem)) {
    if (isCuratedCollection(contentItem.collection)) {
      if (isValidCuratedCollection(contentItem.collection)) dataSources.push(contentItem.collection.dataSource)
      else logger().error(`Curated collection is missing "dataSource"`, { contentItem })
    }
    return dataSources
  }

  if (isCollectionModel(contentItem) && contentItem.dataSource) {
    dataSources.push(contentItem.dataSource)
    return dataSources
  }

  if (isContentExperiment(contentItem)) {
    const experiment = contentItem

    const experimentDataSources =
      experiment?.variantsCollection?.items
        .filter((item) => item?.contentItemsCollection?.items.some(isCollectionModel))
        .flatMap((item) => item?.contentItemsCollection?.items)
        .filter(isCollectionModel)
        .map((collection) => collection.dataSource)
        .filter(isDefined) ?? []

    return dataSources.concat(experimentDataSources)
  }

  return dataSources
}

function isContentExperiment(contentItem?: Maybe<PageContentItem>): contentItem is ContentExperiment {
  return isString(contentItem?.__typename) && contentItem?.__typename === 'ContentExperiment'
}

const startWatchingOrder = [
  slugs.sweetwater,
  slugs.surprisedByOxford,
  slugs.somethingToStandForWithMikeRowe,
  slugs.theShift,
  slugs.cabrini,
  slugs.youngDavid,
  slugs.soundOfHopeTheStoryOfPossumTrot,
  slugs.sight,
  slugs.soundOfFreedom,
  slugs.homestead,
  slugs.wingfeatherSaga,
  slugs.tuttleTwins,
  slugs.hisOnlySon,
  slugs.afterDeath,
  slugs.testament,
  slugs.dryBar,
  slugs.jungleBeat,
  slugs.freelancers,
  slugs.riot,
  slugs.playingForEternity,
  slugs.lightwise,
]

export function mapStartWatchingToContentLinks(
  projects: StartWatchingProject[],
  { locale }: MapDataOptions = { locale: locales.defaultLocale },
): LinkViewModel[] {
  const startWatchingLinks = projects?.map((project) => {
    if (!project || !project.slug || isProjectExcludedFromStartWatching(project.slug)) return

    // Use normalizeProjectSeasons to transform project.title.seasons into project.seasons
    const normalizedProject = normalizeProjectSeasons(project)
    const season = normalizedProject?.seasons?.[0]
    if (!season) return

    const episode = season.episodes?.[0]
    if (!episode?.guid) return

    const index = startWatchingOrder.findIndex((slug) => slug === project.slug)
    const sortOrder = index >= 0 ? index : 0 // default to the front of the list with the assumption that promoting new content is useful to users.

    return {
      name: project.name ?? undefined,
      alt: project.name ?? undefined,
      imageUrl: episode.posterLandscapeCloudinaryPath,
      linkUrl: buildLinkToEpisode(
        {
          projectSlug: project.slug,
          episodeNumber: episode.episodeNumber,
          subtitle: episode.subtitle,
          seasonNumber: episode.seasonNumber,
          guid: episode.guid,
        },
        { disableDecoration: locale !== locales.defaultLocale },
      ),
      label: episode.subtitle ?? undefined,
      logoUrl: project.logoCloudinaryPath,
      sortOrder,
      track: {
        eventName: 'Clicked Start Watching' as const,
        payload: {
          projectSlug: project.slug,
          episodeNumber: episode.episodeNumber,
          seasonNumber: episode.seasonNumber,
          guid: episode.guid,
        },
      },
      metadata: {
        episode,
        project,
      },
    }
  })

  return startWatchingLinks.filter(isDefined).sort((a, b) => {
    const aso = a.sortOrder ?? 1000
    const bso = b.sortOrder ?? 1000

    if (aso === bso) return 0
    return aso > bso ? 1 : -1
  })
}
